import { useTranslations } from "@acto/web-common/locales";
import { MANAGEMENT_TODO_TYPES } from "../../../utils/managementTodoTypes";
import { PeriodOption, DateRange } from "../../DateRangeSelector/types";
import { periodToDateRange } from "../../../utils/periodToDateRange";
import { usePeriodLabel } from "../../DateRangeSelector/DateRangeSelector";
import { useSingleEmployee } from "@acto/taskpane/core/data-access/team/useSingleEmployee";
import { type MarkedDoneItem, useMarkedDoneData } from "@acto/taskpane/core/data-access/team/useMarkedDoneData";
import { CompletedAppointmentsSidePanel } from "../CompletedAppointmentsSidePanel/CompletedAppointmentsSidePanel";
import { CompletedAppointmentsSidePanelSkeleton } from "../CompletedAppointmentsSidePanel/CompletedAppointmentsSidePanelSkeleton";

interface EmployeeMarkedDoneSidePanelProps {
  employeeId: string;
  employeeName: string;
  selectedPeriod?: PeriodOption;
  selectedCustomDateRange?: DateRange;
  onCustomerClick?: (customerId: string, customerName: string) => void;
}

export const EmployeeMarkedDoneSidePanel = ({
  employeeId,
  employeeName,
  selectedPeriod = PeriodOption.LAST_MONTH,
  selectedCustomDateRange,
  onCustomerClick,
}: EmployeeMarkedDoneSidePanelProps) => {
  const t = useTranslations("managementView.markedDone");
  const effectiveDateRange = periodToDateRange(selectedPeriod, selectedCustomDateRange);
  const periodLabel = usePeriodLabel(selectedPeriod, selectedCustomDateRange);

  const { employee, isPending, isFetching } = useSingleEmployee({
    employeeId,
    dateRange: effectiveDateRange,
    todoTypes: MANAGEMENT_TODO_TYPES,
    withMarkedDone: true,
  });

  const data = useMarkedDoneData(employee?.markedDone ?? null, employeeName, periodLabel);

  if (isPending || isFetching) {
    return <CompletedAppointmentsSidePanelSkeleton />;
  }

  if (!data) {
    return null;
  }

  const describe = (item: MarkedDoneItem): string => {
    const followUp = item.reportExcerpt ?? t("noFollowUp");
    return item.channel ? `${t(`channel.${item.channel}`)} · ${followUp}` : followUp;
  };

  return (
    <CompletedAppointmentsSidePanel
      heading={t("title")}
      countSuffix={t("count", { count: data.count })}
      data={{
        entityName: data.employeeName,
        dateRange: data.period,
        completedCount: data.count,
        appointments: data.items.map((item) => ({
          id: item.id,
          date: item.date,
          title: item.clientName,
          clientExternalReference: item.clientExternalReference,
          todoCount: 0,
          description: describe(item),
          clientId: item.clientId,
          clientAccessible: item.clientAccessible,
          noteId: null,
        })),
      }}
      onCustomerClick={onCustomerClick}
    />
  );
};
